/**
 * Shared site-location resolver for the SignalSentry client app.
 *
 * 2026-05-29 (#182): unifies the dual-source site-location reads that had
 * drifted across the SPA. A site row can carry coordinates in TWO places:
 *
 *   1. `location_obj` - the Phase 12 (Plan 12-02) denormalized snapshot of
 *      the assigned `locations` row: `{ id, name, building_code, lat, lng,
 *      address }`. This is the canonical source of truth going forward.
 *   2. `location` - the legacy Phase 11.1 JSONB bag persisted directly on
 *      the `sites` row: `{ lat?, lng?, address? }` (plus passthrough hints).
 *
 * Before this module, surfaces disagreed about which source to read:
 *   - SiteOverviewTab + SitesMapView read `location_obj` ONLY, so a site
 *     with only the legacy JSONB location was invisible on those maps.
 *   - SitesTable + FrozenMapSnapshot read the JSONB `location` ONLY.
 *   - SiteZonesTab read BOTH (the original canonical resolver, lifted here).
 *
 * That drift meant a single site could appear on one surface and vanish on
 * another depending on which column the on-site / cloud sync had populated.
 * Routing every coordinate + address read through these two helpers removes
 * the drift: a site resolves identically everywhere, preferring the newer
 * `location_obj` and falling back to the legacy JSONB so nothing that
 * worked before regresses.
 *
 * Precedence (both helpers): `location_obj` first, legacy `location` JSONB
 * second, else null. The coordinate resolver is NaN / bounds-safe like
 * {@link formatCoords}: a source whose lat/lng is non-finite is treated as
 * "no usable coordinates" and the resolver continues to the next source.
 */

/**
 * Denormalized `location_obj` shape. Structurally matches the
 * `LocationDenorm` Zod type (now/schemas.ts) so any parsed `SiteDetail` /
 * `SiteAdmin` row is assignable without a cast. `name` + `lat` + `lng` are
 * required (the wire always populates them when the object is present);
 * `building_code` + `address` are nullable per the wire contract.
 */
export interface SiteLocationObjLike {
  /** UUID of the location row in fleet Postgres. */
  id: string;
  /** Display name, e.g. "Pantex Building 12-146". */
  name: string;
  /** Short disambiguator, e.g. "12-146"; null when unset. */
  building_code: string | null;
  /** WGS84 latitude in degrees. */
  lat: number;
  /** WGS84 longitude in degrees. */
  lng: number;
  /** Optional street-level address; null when unset. */
  address: string | null;
}

/**
 * Legacy JSONB `location` bag persisted on the `sites` row. All fields are
 * optional because legacy / partially-populated rows are common.
 */
export interface SiteLocationJsonbLike {
  /** WGS84 latitude in degrees, when present. */
  lat?: number;
  /** WGS84 longitude in degrees, when present. */
  lng?: number;
  /** Street-level address, when present. */
  address?: string;
}

/**
 * Minimal structural shape a site needs to expose for the resolvers. Both
 * the admin `SiteDetail` / `SiteAdmin` and the public `SiteDetail` (event
 * page) are assignable to this without a cast.
 */
export interface SiteLocationSource {
  /** Phase 12 denormalized location snapshot; null / absent until assigned. */
  location_obj?: SiteLocationObjLike | null;
  /** Legacy Phase 11.1 JSONB location bag; null / absent on legacy rows. */
  location?: SiteLocationJsonbLike | null;
  /** Site display name, used as the coordinate label for the JSONB branch. */
  name?: string | null;
}

/**
 * Result of {@link resolveSiteCoords}. `label` is a human-readable name for
 * the resolved location, ready to drop into a map marker / pin popup.
 */
export interface ResolvedSiteCoords {
  /** WGS84 latitude in degrees (guaranteed finite). */
  lat: number;
  /** WGS84 longitude in degrees (guaranteed finite). */
  lng: number;
  /**
   * Display label: for `location_obj` it is `"Name (building_code)"`
   * (building_code omitted when null); for the JSONB fallback it is the
   * site name, or the literal "Sentry" when the site has no name.
   */
  label: string;
}

/**
 * True only for a real, finite WGS84-plausible number. Mirrors the bounds
 * guard in {@link formatCoords} so a garbage lat/lng (NaN, Infinity, or a
 * physically impossible value from a parse bug) never produces a marker the
 * maplibre constructor would choke on.
 *
 * @param n Candidate latitude OR longitude.
 * @param max Inclusive absolute bound (90 for latitude, 180 for longitude).
 * @returns Whether `n` is a finite number within [-max, max].
 */
function isUsableDegree(n: unknown, max: number): n is number {
  return typeof n === 'number' && Number.isFinite(n) && n >= -max && n <= max;
}

/**
 * Resolve a site's display coordinates from whichever source is populated,
 * preferring the canonical `location_obj` and falling back to the legacy
 * JSONB `location`. Returns null when NEITHER source has a usable
 * (finite, in-bounds) lat/lng pair, so callers can render a "no location"
 * placeholder or skip a map marker.
 *
 * This is the single source of truth for "where is this site on a map?"
 * across every SPA surface (zones preview, overview satellite map, sentries
 * map view, sites table tooltip, frozen event snapshot).
 *
 * @param site Any object exposing `location_obj` / `location` / `name`, or
 *   null / undefined (e.g. an in-flight React Query result).
 * @returns Resolved `{ lat, lng, label }`, or null when no usable source.
 */
export function resolveSiteCoords(
  site: SiteLocationSource | null | undefined,
): ResolvedSiteCoords | null {
  if (!site) return null;

  // 1. Canonical: the Phase 12 denormalized location_obj.
  const lo = site.location_obj;
  if (lo && isUsableDegree(lo.lat, 90) && isUsableDegree(lo.lng, 180)) {
    const label = `${lo.name}${lo.building_code ? ` (${lo.building_code})` : ''}`;
    return { lat: lo.lat, lng: lo.lng, label };
  }

  // 2. Fallback: the legacy JSONB location bag on the sites row.
  const loc = site.location;
  if (loc && isUsableDegree(loc.lat, 90) && isUsableDegree(loc.lng, 180)) {
    return { lat: loc.lat, lng: loc.lng, label: site.name ?? 'Sentry' };
  }

  // 3. Neither source carries usable coordinates.
  return null;
}

/**
 * Resolve a site's human-readable street address with the same precedence
 * as {@link resolveSiteCoords}: the denormalized `location_obj.address`
 * first, the legacy JSONB `location.address` second, else null.
 *
 * Address resolution is intentionally INDEPENDENT of coordinate resolution:
 * a site can carry an address without lat/lng (or vice-versa), so the
 * SitesTable "Location" column can still show an address for a row that has
 * no plottable coordinates.
 *
 * @param site Any object exposing `location_obj` / `location`, or null.
 * @returns The first non-empty address found, or null when none is set.
 */
export function resolveSiteAddress(
  site: SiteLocationSource | null | undefined,
): string | null {
  if (!site) return null;
  // location_obj.address is nullable; treat empty string as "unset" too.
  if (site.location_obj && site.location_obj.address) {
    return site.location_obj.address;
  }
  if (site.location && site.location.address) {
    return site.location.address;
  }
  return null;
}
